import { Box, Divider, Typography, Stack, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { useTenantTranslation } from "hooks/formatters";
import { DailyVideoData } from "src/store/api/generatedApi";
import dayjs from "dayjs";

type DailyVideoStaplesProps = Readonly<{
  dailyVideoData: readonly DailyVideoData[];
}>;

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const formatPercentage = (value: number, total: number): string => {
  if (total === 0) return "0%";
  return `${((value / total) * 100).toFixed(2)}%`;
};

export default function DailyVideoStaples({
  dailyVideoData,
}: DailyVideoStaplesProps) {
  const { t } = useTenantTranslation();

  return (
    <Stack direction="row" spacing={2} alignItems="flex-end">
      {dailyVideoData.map((day, index) => {
        // Sum per-camera values to get device totals
        const expectedSeconds = Object.values(
          day.per_camera_expected_seconds
        ).reduce((sum, val) => sum + val, 0);
        const liveSeconds = Object.values(day.per_camera_live_seconds).reduce(
          (sum, val) => sum + val,
          0
        );
        const backfilledSeconds = Object.values(
          day.per_camera_backfilled_seconds
        ).reduce((sum, val) => sum + val, 0);

        const totalCaptured = liveSeconds + backfilledSeconds;
        const coveragePercent =
          expectedSeconds > 0 ? (totalCaptured / expectedSeconds) * 100 : 0;

        const livePercent =
          expectedSeconds > 0 ? (liveSeconds / expectedSeconds) * 100 : 0;

        const backfilledPercent =
          expectedSeconds > 0 ? (backfilledSeconds / expectedSeconds) * 100 : 0;

        const maxHeight = 100;
        const stapleHeight = maxHeight;
        const liveHeightPercent = Math.min(livePercent, 100);
        const backfilledHeightPercent = Math.min(
          backfilledPercent,
          100 - liveHeightPercent
        );

        const tenantDay = dayjs(day.date.split("T")[0]);
        const isToday = index === dailyVideoData.length - 1;
        const dayLabel = isToday ? t("today") : tenantDay.format("MMM D");

        return (
          <motion.div
            key={day.date}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2" fontWeight="fontWeightSemiBold">
                    {tenantDay.format("MMM D, YYYY")}{" "}
                    {isToday ? `(${t("today")})` : null}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {t("liveVideo")}: {formatDuration(liveSeconds)} (
                    {formatPercentage(liveSeconds, expectedSeconds)})
                  </Typography>
                  <Typography variant="caption" display="block">
                    {t("backfilledVideo")}: {formatDuration(backfilledSeconds)}{" "}
                    ({formatPercentage(backfilledSeconds, expectedSeconds)})
                  </Typography>
                  <Typography variant="caption" display="block">
                    {t("totalCoverage")}:{" "}
                    {formatPercentage(totalCaptured, expectedSeconds)}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    {t("expectedDuration")}: {formatDuration(expectedSeconds)}
                  </Typography>

                  {/* Per-camera breakdown */}
                  {Object.keys(day.per_camera_expected_seconds).length > 0 && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography
                        variant="caption"
                        display="block"
                        fontWeight="fontWeightSemiBold"
                      >
                        {t("perCameraBreakdown")}:
                      </Typography>
                      {Object.entries(day.per_camera_expected_seconds).map(
                        ([camera, expSecs]) => {
                          const liveSecs =
                            day.per_camera_live_seconds[camera] ?? 0;
                          const backSecs =
                            day.per_camera_backfilled_seconds[camera] ?? 0;
                          const camTotal = liveSecs + backSecs;
                          const camCoverage =
                            expSecs > 0 ? (camTotal / expSecs) * 100 : 0;

                          return (
                            <Typography
                              key={camera}
                              variant="caption"
                              display="block"
                            >
                              {camera}: {formatDuration(camTotal)} /{" "}
                              {formatDuration(expSecs)} (
                              {camCoverage.toFixed(1)}
                              %)
                            </Typography>
                          );
                        }
                      )}
                    </>
                  )}
                </Box>
              }
              placement="top"
            >
              <Box sx={{ textAlign: "center", cursor: "pointer" }}>
                {/* Staple */}
                <Box
                  sx={{
                    width: 48,
                    height: stapleHeight,
                    borderRadius: 1,
                    position: "relative",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "grey.100",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Empty space at top if total coverage < 100% */}
                  {coveragePercent < 100 && (
                    <Box
                      sx={{
                        flex: `0 0 ${100 - coveragePercent}%`,
                        backgroundColor: "transparent",
                      }}
                    />
                  )}

                  {/* Backfilled video portion */}
                  {backfilledHeightPercent > 0 && (
                    <Box
                      sx={{
                        flex: `0 0 ${backfilledHeightPercent}%`,
                        backgroundColor: "#2196F3", // Blue for backfill
                      }}
                    />
                  )}

                  {/* Live video portion */}
                  {liveHeightPercent > 0 && (
                    <Box
                      sx={{
                        flex: `0 0 ${liveHeightPercent}%`,
                        backgroundColor: "#4CAF50", // Green for live
                      }}
                    />
                  )}
                </Box>

                {/* Day label */}
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    display: "block",
                    fontWeight: isToday
                      ? "fontWeightSemiBold"
                      : "fontWeightRegular",
                    color: isToday ? "primary.main" : "text.secondary",
                  }}
                >
                  {dayLabel}
                </Typography>
              </Box>
            </Tooltip>
          </motion.div>
        );
      })}
    </Stack>
  );
}
